import React, { useState, useEffect } from 'react';
import { 
  Calendar, 
  Plus, 
  Search, 
  Filter,
  Clock,
  MapPin,
  User,
  Edit,
  Trash2,
  Eye,
  Share2,
  AlertCircle,
  CheckCircle,
  XCircle,
  Phone
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

interface Appointment {
  id: string;
  provider_id?: string;
  date: string;
  time?: string;
  type: string;
  status: 'scheduled' | 'completed' | 'cancelled' | 'rescheduled';
  notes?: string;
  location?: string;
  created_at: string;
}

export function MobileAppointments() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('all');

  // Load appointments
  useEffect(() => {
    const loadAppointments = async () => {
      if (!user) return;

      try {
        setLoading(true);
        setError(null);

        const { data, error } = await supabase
          .from('health_appointments')
          .select('*')
          .eq('user_id', user.id)
          .order('date', { ascending: true });

        if (error) throw error;
        setAppointments(data || []);

      } catch (err) {
        console.error('Error loading appointments:', err);
        setError('Failed to load appointments');
      } finally {
        setLoading(false);
      }
    };

    loadAppointments();
  }, [user]);

  const handleViewAppointment = (appointmentId: string) => {
    console.log('View appointment:', appointmentId);
  };

  const handleEditAppointment = (appointmentId: string) => {
    console.log('Edit appointment:', appointmentId);
  };

  const handleDeleteAppointment = (appointmentId: string) => {
    console.log('Delete appointment:', appointmentId);
  };

  const handleAddAppointment = () => {
    console.log('Add new appointment');
  };

  const handleCallProvider = (phone?: string) => {
    if (phone) {
      console.log('Call provider:', phone);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString: string, timeString: string) => {
    const date = new Date(`${dateString}T${timeString}`);
    return date.toLocaleString('en-AU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'scheduled':
        return 'text-blue-600 bg-blue-100';
      case 'completed':
        return 'text-green-600 bg-green-100';
      case 'cancelled':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type.toLowerCase()) {
      case 'checkup':
        return 'text-green-600 bg-green-100';
      case 'consultation':
        return 'text-blue-600 bg-blue-100';
      case 'procedure':
        return 'text-purple-600 bg-purple-100';
      case 'test':
        return 'text-orange-600 bg-orange-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const filteredAppointments = appointments.filter(appointment => {
    const matchesSearch = appointment.type.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (appointment.provider_id && appointment.provider_id.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesFilter = selectedFilter === 'all' || appointment.status === selectedFilter;
    return matchesSearch && matchesFilter;
  });

  const upcomingAppointments = appointments.filter(a => 
    a.status === 'scheduled' && new Date(a.date) > new Date()
  );
  const todayAppointments = appointments.filter(a => 
    a.status === 'scheduled' && 
    new Date(a.date).toDateString() === new Date().toDateString()
  );

  if (loading) {
    return (
      <div className="p-4 pb-safe space-y-4">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded"></div>
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-20 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 pb-safe space-y-4">
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
          <AlertCircle size={16} className="text-red-500" />
          <p className="text-red-600 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 pb-safe space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Appointments</h2>
          <p className="text-sm text-gray-600">Schedule and manage your appointments</p>
        </div>
        <Button size="sm" onClick={handleAddAppointment}>
          <Plus size={16} className="mr-2" />
          Add
        </Button>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 gap-3">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Calendar size={20} className="text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Upcoming</p>
              <p className="text-lg font-semibold">{upcomingAppointments.length}</p>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <Clock size={20} className="text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Today</p>
              <p className="text-lg font-semibold">{todayAppointments.length}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Search */}
      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          placeholder="Search appointments..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-idecide-primary focus:border-transparent"
        />
      </div>

      {/* Filter */}
      <div className="flex gap-2 overflow-x-auto">
        <Button
          variant={selectedFilter === 'all' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setSelectedFilter('all')}
          className="whitespace-nowrap"
        >
          All
        </Button>
        <Button
          variant={selectedFilter === 'scheduled' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setSelectedFilter('scheduled')}
          className="whitespace-nowrap"
        >
          Scheduled
        </Button>
        <Button
          variant={selectedFilter === 'completed' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setSelectedFilter('completed')}
          className="whitespace-nowrap"
        >
          Completed
        </Button>
        <Button
          variant={selectedFilter === 'cancelled' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setSelectedFilter('cancelled')}
          className="whitespace-nowrap"
        >
          Cancelled
        </Button>
      </div>

      {/* Appointments List */}
      <div className="space-y-3">
        {filteredAppointments.length === 0 ? (
          <Card className="p-8 text-center">
            <Calendar size={48} className="text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Appointments</h3>
            <p className="text-gray-600 mb-4">Start by scheduling your first appointment</p>
            <Button onClick={handleAddAppointment}>
              <Plus size={16} className="mr-2" />
              Schedule Appointment
            </Button>
          </Card>
        ) : (
          filteredAppointments.map((appointment) => (
            <Card key={appointment.id} className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <Calendar size={16} className="text-purple-500" />
                    <h3 className="font-medium text-gray-900">{appointment.type}</h3>
                  </div>
                  {appointment.provider_id && (
                    <p className="text-sm text-gray-600 flex items-center gap-1">
                      <User size={12} />
                      {appointment.provider_id}
                    </p>
                  )}
                  <p className="text-xs text-gray-500 flex items-center gap-1">
                    <Clock size={12} />
                    {appointment.time ? formatDateTime(appointment.date, appointment.time) : formatDate(appointment.date)}
                  </p>
                  {appointment.location && (
                    <p className="text-xs text-gray-500 flex items-center gap-1">
                      <MapPin size={12} />
                      {appointment.location}
                    </p>
                  )}
                  {appointment.notes && (
                    <p className="text-xs text-gray-500 mt-1">{appointment.notes}</p>
                  )}
                </div>
                <div className="flex gap-1">
                  <Button 
                    size="sm" 
                    variant="ghost"
                    onClick={() => handleViewAppointment(appointment.id)}
                  >
                    <Eye size={14} />
                  </Button>
                  <Button 
                    size="sm" 
                    variant="ghost"
                    onClick={() => handleEditAppointment(appointment.id)}
                  >
                    <Edit size={14} />
                  </Button>
                </div>
              </div>
              
              {/* Status and Type Badges */}
              <div className="mt-3 flex items-center justify-between">
                <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(appointment.status)}`}>
                  {appointment.status}
                </span>
                <span className={`px-2 py-1 rounded-full text-xs ${getTypeColor(appointment.type)}`}>
                  {appointment.type}
                </span>
              </div>
            </Card>
          ))
        )}
      </div>
    </div>
  );
} 